/**
 * SettingsRazorpayCard — Razorpay Payment-Link integration card.
 * Extracted from SettingsPage.jsx (Iter 140, Feb 2026).
 */
import { Loader2, Save, CheckCircle, AlertTriangle, ExternalLink, Eye, EyeOff, Download, Send, Trash2, RefreshCw } from "lucide-react";
import { useState } from "react";

// Ensure every existing row has a stable `_uid` so React keys stay consistent
// when appending new recipient/reason rows.
const _withUid = (items) =>
  (items || []).map((it) =>
    it && it._uid
      ? it
      : {
          ...it,
          _uid:
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `uid-${Math.random().toString(36).slice(2)}-${Date.now()}`,
        }
  );

// Helper component for Razorpay key entry.
const RazorpayKeyForm = ({ keyId, setKeyId, keySecret, setKeySecret, onSave, saving }) => {
  const [revealSecret, setRevealSecret] = useState(false);
  return (
    <div className="space-y-2">
      <label className="block">
        <span className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wide mb-1 block">Key Id</span>
        <input
          type="text"
          value={keyId}
          onChange={(e) => setKeyId(e.target.value)}
          placeholder="rzp_live_XXXXXXXXXXXX"
          className="w-full px-3 py-2 border rounded text-xs font-mono"
          data-testid="razorpay-key-id-input"
          autoComplete="off"
        />
      </label>
      <label className="block">
        <span className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wide mb-1 block">Key Secret</span>
        <div className="relative">
          <input
            type={revealSecret ? "text" : "password"}
            value={keySecret}
            onChange={(e) => setKeySecret(e.target.value)}
            placeholder="••••••••••••••••••••"
            className="w-full px-3 py-2 pr-20 border rounded text-xs font-mono"
            data-testid="razorpay-key-secret-input"
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setRevealSecret((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-[--accent] hover:underline"
            data-testid="razorpay-reveal-secret-btn"
          >
            {revealSecret ? "Hide" : "Show"}
          </button>
        </div>
      </label>
      <button
        onClick={onSave}
        disabled={saving || !keyId.trim() || !keySecret.trim()}
        className="btn-primary w-full text-xs py-2 disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="razorpay-save-btn"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
        {saving ? "Saving…" : "Save Razorpay Keys"}
      </button>
    </div>
  );
};

export default function SettingsRazorpayCard({
  user,
  razorpayStatus,
  razorpayKeyId, setRazorpayKeyId,
  razorpayKeySecret, setRazorpayKeySecret,
  saveRazorpay, savingRazorpay, clearRazorpay,
  showRazorpayForm, setShowRazorpayForm,
  // Order export action lives in the same JSX block (top-right of card).
  exporting, handleExportOrders,
  // teamNotify (Notifications card lives in this same JSX block — passed
  // through so we don't have to split it out of the original layout).
  teamNotify, setTeamNotify, saveTeamNotify, savingTeamNotify,
}) {
  const [showSecret, setShowSecret] = useState(false);
  return (
    <>
        {/* Razorpay payment-link integration */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="bento-card" data-testid="razorpay-settings-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#072654]/10 rounded-md">
                <svg width="20" height="20" viewBox="0 0 24 24" className="text-[#072654]" fill="currentColor" aria-hidden>
                  <path d="M18.3 3L14 16h-3l2.5-7.5L8 16H5L9.3 3H18.3z"/>
                </svg>
              </div>
              <h2
                className="text-xl font-medium text-[#1C1917]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Razorpay (Payment Links)
              </h2>
              {razorpayStatus?.configured && (
                <span className="ml-auto text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full" data-testid="razorpay-connected-badge">
                  ✓ Connected
                </span>
              )}
            </div>

            {razorpayStatus?.configured ? (
              <div className="space-y-3">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-900 space-y-1" data-testid="razorpay-connected-info">
                  <div><span className="font-bold">Key ID:</span> <code className="bg-white px-1 rounded">{razorpayStatus.key_id_masked}</code></div>
                  <div><span className="font-bold">Source:</span> {razorpayStatus.source === "database" ? "Database (this environment)" : "Environment variable"}</div>
                  <div className="text-[11px] text-emerald-800">Agents can now generate payment links from any lead.</div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowRazorpayForm((v) => !v)}
                    className="btn-secondary flex-1 flex items-center justify-center gap-2 text-sm"
                    data-testid="razorpay-update-btn"
                  >
                    <RefreshCw size={14} /> {showRazorpayForm ? "Cancel" : "Update keys"}
                  </button>
                  <button
                    onClick={clearRazorpay}
                    disabled={savingRazorpay}
                    className="text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg px-3 py-2 disabled:opacity-50"
                    data-testid="razorpay-clear-btn"
                  >
                    Clear
                  </button>
                </div>
                {showRazorpayForm && (
                  <RazorpayKeyForm
                    keyId={razorpayKeyId}
                    setKeyId={setRazorpayKeyId}
                    keySecret={razorpayKeySecret}
                    setKeySecret={setRazorpayKeySecret}
                    onSave={saveRazorpay}
                    saving={savingRazorpay}
                  />
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 space-y-2">
                  <p className="font-bold">One-time setup:</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Log in to <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="underline text-amber-900 font-medium">dashboard.razorpay.com/app/keys</a></li>
                    <li>Click <b>Generate Key</b> (or copy an existing one)</li>
                    <li>Copy the <b>Key Id</b> (starts with <code className="bg-white px-1 rounded">rzp_live_</code> or <code className="bg-white px-1 rounded">rzp_test_</code>) and the <b>Key Secret</b></li>
                    <li>Paste them below and click <b>Save</b></li>
                  </ol>
                </div>
                <RazorpayKeyForm
                  keyId={razorpayKeyId}
                  setKeyId={setRazorpayKeyId}
                  keySecret={razorpayKeySecret}
                  setKeySecret={setRazorpayKeySecret}
                  onSave={saveRazorpay}
                  saving={savingRazorpay}
                />
                <p className="text-[11px] text-[#A8A29E] italic">
                  Keys are stored encrypted in the database — you only need to save them once per environment (preview and live are separate).
                </p>
              </div>
            )}
          </div>
        )}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="bento-card" data-testid="team-notify-settings-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-50 rounded-md">
                <Send size={20} className="text-indigo-700" />
              </div>
              <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>
                Team notifications
              </h2>
            </div>
            <p className="text-xs text-[--text-secondary] mb-4 leading-relaxed">
              Recipients & reasons for the chat-header <strong>Notify team</strong> button. Agents can email any chat to one of these teams in one click, picking from the reason dropdown below.
            </p>

            {/* Recipients */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[--text-muted]">Recipients</label>
                <button
                  type="button"
                  className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
                  onClick={() => setTeamNotify((prev) => ({
                    ...prev,
                    recipients: [..._withUid(prev.recipients), {
                      label: "", email: "",
                      _uid: (typeof crypto !== "undefined" && crypto.randomUUID)
                        ? crypto.randomUUID()
                        : `uid-${Math.random().toString(36).slice(2)}-${Date.now()}`,
                    }],
                  }))}
                  data-testid="add-team-recipient-btn"
                >+ Add recipient</button>
              </div>
              <div className="space-y-2">
                {(teamNotify.recipients || []).map((r, idx) => (
                  <div key={r._uid || `recip-${idx}`} className="flex gap-2 items-center" data-testid={`team-recipient-row-${idx}`}>
                    <input
                      placeholder="Label (e.g. Sales)"
                      value={r.label || ""}
                      onChange={(e) => {
                        const next = [...(teamNotify.recipients || [])];
                        next[idx] = { ...next[idx], label: e.target.value };
                        setTeamNotify({ ...teamNotify, recipients: next });
                      }}
                      className="flex-1 border-2 border-[--border] rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-300"
                    />
                    <input
                      placeholder="email@exoticindia.com"
                      type="email"
                      value={r.email || ""}
                      onChange={(e) => {
                        const next = [...(teamNotify.recipients || [])];
                        next[idx] = { ...next[idx], email: e.target.value };
                        setTeamNotify({ ...teamNotify, recipients: next });
                      }}
                      className="flex-[2] border-2 border-[--border] rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-300"
                    />
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-800 px-2"
                      onClick={() => {
                        const next = (teamNotify.recipients || []).filter((_, i) => i !== idx);
                        setTeamNotify({ ...teamNotify, recipients: next });
                      }}
                      data-testid={`remove-team-recipient-${idx}`}
                    ><Trash2 size={14} /></button>
                  </div>
                ))}
                {(teamNotify.recipients || []).length === 0 && (
                  <p className="text-[11px] text-[--text-muted] italic">No recipients yet — click <strong>+ Add recipient</strong>.</p>
                )}
              </div>
            </div>

            {/* Reasons */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[--text-muted]">Reasons (dropdown)</label>
                <button
                  type="button"
                  className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
                  onClick={() => setTeamNotify((prev) => ({
                    ...prev,
                    reasons: [..._withUid(prev.reasons), {
                      value: "", label: "", icon: "",
                      _uid: (typeof crypto !== "undefined" && crypto.randomUUID)
                        ? crypto.randomUUID()
                        : `uid-${Math.random().toString(36).slice(2)}-${Date.now()}`,
                    }],
                  }))}
                  data-testid="add-team-reason-btn"
                >+ Add reason</button>
              </div>
              <div className="space-y-2">
                {(teamNotify.reasons || []).map((r, idx) => (
                  <div key={r._uid || `reason-${idx}`} className="flex gap-2 items-center" data-testid={`team-reason-row-${idx}`}>
                    <input
                      placeholder="🚚"
                      value={r.icon || ""}
                      onChange={(e) => {
                        const next = [...(teamNotify.reasons || [])];
                        next[idx] = { ...next[idx], icon: e.target.value };
                        setTeamNotify({ ...teamNotify, reasons: next });
                      }}
                      className="w-12 text-center border-2 border-[--border] rounded-xl px-2 py-2 text-xs outline-none focus:border-indigo-300"
                    />
                    <input
                      placeholder="key (e.g. shipping_issue)"
                      value={r.value || ""}
                      onChange={(e) => {
                        const next = [...(teamNotify.reasons || [])];
                        next[idx] = { ...next[idx], value: e.target.value.toLowerCase().replace(/\s+/g, "_") };
                        setTeamNotify({ ...teamNotify, reasons: next });
                      }}
                      className="flex-1 border-2 border-[--border] rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-300"
                    />
                    <input
                      placeholder="Label shown in dropdown"
                      value={r.label || ""}
                      onChange={(e) => {
                        const next = [...(teamNotify.reasons || [])];
                        next[idx] = { ...next[idx], label: e.target.value };
                        setTeamNotify({ ...teamNotify, reasons: next });
                      }}
                      className="flex-[2] border-2 border-[--border] rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-300"
                    />
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-800 px-2"
                      onClick={() => {
                        const next = (teamNotify.reasons || []).filter((_, i) => i !== idx);
                        setTeamNotify({ ...teamNotify, reasons: next });
                      }}
                      data-testid={`remove-team-reason-${idx}`}
                    ><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={saveTeamNotify}
              disabled={savingTeamNotify}
              className="px-5 py-2.5 rounded-full text-sm font-bold text-white disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #4F46E5, #312E81)" }}
              data-testid="save-team-notify-btn"
            >{savingTeamNotify ? "Saving…" : "Save team notifications"}</button>
          </div>
        )}
        {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "leader") && (
          <div className="bento-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#BC4B35]/10 rounded-md">
                <Download size={20} className="text-[#BC4B35]" />
              </div>
              <h2
                className="text-xl font-medium text-[#1C1917]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Data Export
              </h2>
            </div>

            <div className="space-y-4">
              <p className="text-[#57534E]">
                Export your CRM data for reporting and analysis.
              </p>

              <button
                onClick={handleExportOrders}
                disabled={exporting}
                className="btn-secondary w-full flex items-center justify-center gap-2"
                data-testid="export-orders-btn"
              >
                {exporting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Download size={18} />
                )}
                {exporting ? "Exporting..." : "Export Orders to CSV"}
              </button>
            </div>
          </div>
        )}
    </>
  );
}
